<template>
    <div class="main-container">
        <v-container fluid class="submit-panel overflow-auto pa-5">
            <h4 class="mb-2">{{ $t('submit_event') }}</h4>
            <p class="tgk-paragraph-text" v-html="$t('publish_infotxt')"></p>

            <ActionBtn v-if="!hidePreview" :href="getUrl(true)" target="tgk_preview" class="mb-5">
                <KpIcon icon-cls="preview" color="white" left></KpIcon>
                <span>{{ $t('preview') }}</span>
            </ActionBtn>

            <!-- info box -->
            <SmallInfoBox :event="record" class="mb-15" />

            <v-row
                v-if="['draft', 'rejected'].indexOf(record.status) >= 0"
                justify="end"
                class="terms-row flex-nowrap mb-5"
            >
                <p
                    class="mt-2 mb-0 tgk-paragraph-text text-right align-center"
                    v-html="
                        $t('agree_usage_rights', {
                            usage_rights_url: getUsageRightsUrl(),
                            privacy_policy_url: getPrivacyPolicyUrl(),
                        })
                    "
                ></p>
                <v-checkbox v-if="!singleConsent" v-model="usageRights" hide-details class="shrink mt-0 ml-2" />
            </v-row>
            <v-row
                v-if="['draft', 'rejected'].indexOf(record.status) >= 0"
                justify="end"
                class="terms-row flex-nowrap mb-5"
            >
                <p
                    class="mt-2 mb-0 tgk-paragraph-text text-right flex-column align-center"
                    v-html="$t('agree_data_policy', { data_policy_url: getDataPolicyUrl() })"
                ></p>
                <v-checkbox v-if="!singleConsent" v-model="dataAgreement" hide-details class="shrink mt-0 ml-2" />
            </v-row>
            <v-row
                v-if="['draft', 'rejected'].indexOf(record.status) >= 0"
                justify="end"
                class="terms-row flex-nowrap mb-5"
            >
                <p
                    class="mt-2 mb-0 tgk-paragraph-text text-right align-center"
                    v-html="$t('agree_term', { terms_policy_url: getTermsPolicyUrl() })"
                ></p>
                <v-checkbox v-if="!singleConsent" v-model="termsAgreement" hide-details class="shrink mt-0 ml-2" />
            </v-row>
            <v-row
                v-if="singleConsent && ['draft', 'rejected'].indexOf(record.status) >= 0"
                justify="end"
                class="terms-row flex-nowrap mb-5"
            >
                <p
                    class="mt-2 mb-0 tgk-paragraph-text text-right align-center"
                    v-html="$t('accept_conditions_above')"
                ></p>
                <v-checkbox v-model="conditionsAgreement" hide-details dense class="shrink ma-0 pa-0" />
            </v-row>

            <v-row class="mt-0" justify="end" style="gap: 10px">
                <ActionBtn v-if="draftAllowed" @click="onUnpublishBtn">
                    <KpIcon icon-cls="pencil" color="white" />
                    <span>{{ $t('draft') }}</span>
                </ActionBtn>
                <ActionBtn v-if="showSubmitBtn" @click="onSubmitBtn">
                    <KpIcon icon-cls="published" color="white" />
                    <span>{{ $t('submit') }}</span>
                </ActionBtn>
            </v-row>
        </v-container>

        <!-- Side panel -->
        <div class="info-panel overflow-auto pa-5">
            <KpAlert v-for="banner in infoboxesSubmit" :key="banner.id" type="info" style="max-width: 600px">
                <h4>{{ banner.title }}</h4>
                <p>{{ banner.comment }}</p>
            </KpAlert>
        </div>
        <ConfirmDialog
            v-model="showPublishDlg"
            :title="$t('publish')"
            :message="$t('publish_sure')"
            @confirm="doSubmit"
        />
        <ConfirmDialog
            v-model="showUnpublishDlg"
            :title="$t('unpublizieren')"
            :message="$t('unpublizieren_sure')"
            @confirm="doUnpublish"
        />
        <EventCheckDialog v-model="showReasonDlg" :reasons="eventCheckReasons" @publish="publishAnyway" />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { Event } from '@/models/Event';
import { instance as api } from '@/helpers/api';
import { formRules, errToStr } from '@/helpers/tools';
import { globalVar, globalParam } from '@/helpers/globals';
import GlobalsMixin from '@/mixins/globals';
import { userIsStaff } from '@/helpers/auth';

import SmallInfoBox from '@/components/event/SmallInfoBox.vue';
import EventCheckDialog from './EventCheckDialog.vue';
import { commitToast } from '@/plugins/toast';
import backRouteMixin from '@/mixins/backroute';

export default {
    name: 'EventEditSubmitPanel',
    components: { SmallInfoBox, EventCheckDialog },
    mixins: [GlobalsMixin, backRouteMixin],

    props: {
        record: {
            type: Event,
            required: true,
        },
        // used to indicate form validity
        value: Boolean,
    },

    data() {
        return {
            showPublishDlg: false,
            showUnpublishDlg: false,
            usageRights: false,
            dataAgreement: false,
            termsAgreement: false,
            conditionsAgreement: false,
            showReasonDlg: false,
            eventCheckReasons: [],
            // singleConsent = true means that only one "I agree" checkbox must be checked, instead of multiple ones.
            singleConsent: globalParam('event.submit_single_consent') === true,
        };
    },

    computed: {
        userIsStaff,
        draftAllowed() {
            return this.record.status !== Event.STATUS_DRAFT;
        },
        showSubmitBtn() {
            return [Event.STATUS_DRAFT, Event.STATUS_REJECTED, Event.STATUS_IMPORTED].includes(this.record.status);
        },
        submitAllowed() {
            const allowedStatus = [Event.STATUS_DRAFT, Event.STATUS_REJECTED];
            return allowedStatus.indexOf(this.record.status) >= 0 && this.allTermsAgreed;
        },
        infoboxesSubmit() {
            return this.$store.state.banner.eventInfoboxes.collection
                ? this.$store.state.banner.eventInfoboxes.collection.models.filter(
                      (banner) => banner.type === 'infobox_event_submit',
                  )
                : [];
        },
        hidePreview() {
            return globalParam('event.hide_preview');
        },
        allTermsAgreed() {
            if (this.singleConsent) {
                return this.conditionsAgreement === true;
            } else {
                return this.usageRights && this.dataAgreement && this.termsAgreement;
            }
        },
    },

    created() {
        this.rules = formRules;
        this.loadEventInfoboxes();
    },

    methods: {
        ...mapActions('banner', ['loadEventInfoboxes']),
        onInput(field, value) {
            this.record.set(field, value);
        },
        getUrl(preview = false) {
            if (preview) {
                return this.record?.detailPreviewUrl;
            }
            return this.record?.detailUrl;
        },
        getImageUrl(record) {
            const ts = new Date().getTime();
            const conf = globalVar('featured_images_config').event.thumb;
            return `${globalVar('imageroot')}/event/${record.id}/${conf.ident}.${conf.format}?ts=${ts}`;
        },
        getUsageRightsUrl() {
            return globalParam('usage_rights_url');
        },
        getPrivacyPolicyUrl() {
            return globalParam('privacy_policy_url');
        },
        getDataPolicyUrl() {
            return globalParam('data_policy_url');
        },
        getTermsPolicyUrl() {
            return globalParam('terms_conditions_url');
        },

        async onSubmitBtn() {
            if (!this.submitAllowed) {
                commitToast({ message: this.$t('accept_terms'), color: 'error' });
                return;
            }
            this.eventCheckReasons = [];
            let reasons = await this.checkEvent(Event.STATUS_REGISTERED);
            if (reasons === false) {
                return;
            }
            if (reasons?.length === 0) {
                this.showPublishDlg = true;
            } else {
                // show reason dialog
                this.eventCheckReasons = reasons;
                this.showReasonDlg = true;
            }
        },

        async doSubmit() {
            await this.setStatus(
                Event.STATUS_REGISTERED,
                [
                    this.usageRights ? 'usageRights' : false,
                    this.dataAgreement ? 'dataAgreement' : false,
                    this.termsAgreement ? 'termsAgreement' : false,
                    this.conditionsAgreement ? 'conditionsAgreement' : false,
                ].filter(Boolean),
            );
            commitToast({ message: this.$t('event_registered'), color: 'success' });

            // route back to wher we came from:
            if (this.getBackRouteConfig()?.route) {
                this.$router.push(this.getBackRouteConfig().route);
            } else {
                this.$router.push({ name: 'event' });
            }
        },

        onUnpublishBtn() {
            this.showUnpublishDlg = true;
        },
        async doUnpublish() {
            await this.setStatus(Event.STATUS_DRAFT);
            commitToast({ message: this.$t('event_draft'), color: 'success' });
        },

        /**
         * This method can be called when the event check was not successful,
         * but the (staff) user would publish the event anyway.
         * It initiates the publish method, even if the checks failed.
         */
        publishAnyway() {
            this.showReasonDlg = false;
            if (this.userIsStaff) {
                this.showPublishDlg = true;
            }
        },

        /**
         * Initiates a check if the event is ready to be published:
         * the backend checks the record for completeness, and returns
         * a list of errors if not.
         */
        async checkEvent(status) {
            try {
                this.$emit('saving', true);
                return this.record.checkStatusChange(status);
            } catch (e) {
                commitToast({ message: errToStr(e), color: 'error' });
                return false;
            } finally {
                this.$emit('saving', false);
            }
        },

        async setStatus(newStatus, legalAcceptances = null) {
            try {
                this.$emit('saving', true);
                await api().post('/Event/status', {
                    event_id: this.record.id,
                    status: newStatus,
                    legalAcceptances,
                });
                this.$emit('reload-event');
            } catch (e) {
                commitToast({ message: errToStr(e), color: 'error' });
            } finally {
                this.$emit('saving', false);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.main-container {
    display: grid;
    grid-template-areas: 'edit info';
    grid-template-rows: auto;
    grid-template-columns: minmax(400px, 800px) minmax(300px, 1fr);
    overflow: auto;
}
.submit-panel {
    grid-area: edit;
}
.info-panel {
    grid-area: info;
    background-color: var(--toolbar-bg-color);
}

.infobox-table {
    width: 100%;
    background-color: var(--toolbar-bg-color);
    td {
        vertical-align: top;
        padding: 5px;
    }
}
.terms-row {
    margin: 12px 0px 4px 0px;

    p {
        max-width: 600px;
    }
}
</style>
